import React from 'react';
import { colorForKind, SpanKindIcon } from 'shared/components/spans/KindTag';
import { HighlightContext, HighlightText } from 'shared/components/HighlightText';

/**
 * RunSearchResults — dropdown of in-run search hits (keyword + semantic) from
 * GET /agent-runs/:id/search. Each row shows the matched entity (run or span),
 * which field matched, a snippet with the matching words marked, and a
 * keyword/semantic badge. Picking a row opens that node with the match shown.
 *
 * The snippet used to be marked by indexOf on the raw query, which meant it
 * only worked when the user had typed the exact phrase present in the text —
 * so "failing users" showed an unmarked snippet even on the row it had
 * matched, and semantic rows were never marked at all. It now marks the same
 * stems the backend matched on, through the same component the destination
 * page uses, so what is highlighted in the dropdown is what will be
 * highlighted when you click through.
 */
const FIELD_LABEL = { input: 'Input', output: 'Output', attributes: 'Attributes', eval: 'Eval' };
const TARGET_LABEL = { run: 'run', span: 'span', conversation: 'conversation' };

export default function RunSearchResults({ results = [], loading, query, terms = [], mode, onPick }) {
  const snippetHighlight = React.useMemo(() => {
    if (terms?.length) return { terms, passage: '', whole: true };
    const q = (query || '').trim();
    return q ? { terms: [q], passage: '', whole: false } : '';
  }, [terms, query]);
  return (
    <div
      className="absolute left-0 right-0 top-full z-[70] mt-1 max-h-[360px] overflow-y-auto rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-lg"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <div className="sticky top-0 flex items-center justify-between border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)] px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
        <span>{loading ? 'Searching…' : `${results.length} result${results.length === 1 ? '' : 's'}`}</span>
        {mode && mode !== 'empty' ? <span className="normal-case">{mode === 'mixed' ? 'keyword + semantic' : mode}</span> : null}
      </div>
      {!loading && results.length === 0 ? (
        <div className="px-3 py-4 text-center text-[12px] text-[color:var(--text-muted)]">
          No matches in this run.
        </div>
      ) : (
        <ul className="py-1">
          {results.map((r) => {
            const isEval = r.matchType === 'eval';
            const kind = r.kind === 'run' || isEval ? 'run' : r.kind;
            const color = colorForKind(kind);
            return (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => onPick(r)}
                  className="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-[color:var(--surface-sunken)]"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="flex h-4 w-4 flex-none items-center justify-center" title={color.label}>
                      <SpanKindIcon kind={kind} size={13} />
                    </span>
                    <span className="truncate text-[12px] font-medium text-[color:var(--text-primary)]">
                      {isEval ? r.name : r.spanId ? r.name : `${r.name} (run)`}
                    </span>
                    <span className="ml-auto flex flex-none items-center gap-1">
                      {!isEval && (
                        <span className="rounded bg-[color:var(--surface-sunken)] px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                          {FIELD_LABEL[r.field] || r.field}
                        </span>
                      )}
                      <span
                        className="rounded px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide"
                        style={r.matchType === 'semantic'
                          ? { backgroundColor: 'var(--feedback-info-bg)', color: 'var(--feedback-info)' }
                          : { backgroundColor: 'var(--surface-sunken)', color: 'var(--text-secondary)' }}
                        title={isEval ? `This ${TARGET_LABEL[r.target] || 'run'}'s eval verdict` : r.matchType === 'semantic' ? `Semantic match · ${Math.round((r.score || 0) * 100)}%` : 'Keyword match'}
                      >
                        {isEval ? `Score · ${TARGET_LABEL[r.target] || 'run'}` : r.matchType === 'semantic' ? '≈ Semantic' : 'Keyword'}
                      </span>
                    </span>
                  </div>
                  <div className="line-clamp-2 pl-5 text-[11px] leading-snug text-[color:var(--text-secondary)]">
                    <HighlightContext.Provider value={snippetHighlight}>
                      <HighlightText text={r.snippet} />
                    </HighlightContext.Provider>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
